import React, {Component, PropTypes} from 'react';
import ReactDOM from 'react-dom';

// Stores and Actions
import zoomerStore from '../../stores/zoomer';
import zoomerActions from '../../actions/zoomer';
import StoreConnect from '../../flux/StoreConnect';
import browserCheck from '../../utils/browserCheck';

// Subviews
import Controls from './controls';

// Drag Controller
import Drag from './drag';

import config from '../../config';

class SIMZoomer extends Component {

  constructor() {
    super();

    this.onClick = this.onClick.bind(this);
    this.onScrollZoom = this.onScrollZoom.bind(this);

    this.start = this.start.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onPinch = this.onPinch.bind(this);
    this.onEnd = this.onEnd.bind(this);

    // setup the drag controller
    this.drgCtrl = new Drag();
    this.drgCtrl.onMove(this.onMove);
    this.drgCtrl.onEnd(this.onEnd);
    this.drgCtrl.onPinch(this.onPinch);

    // reset the click disabler (disables clicks when dragging)
    this.resetClickDisabler();
  }

  componentDidMount() {
    // cache node searching, probably improves performance
    this._DOMNode = ReactDOM.findDOMNode(this);

    setTimeout(() => {
      zoomerActions.emit(zoomerActions.EVENTS.REGISTER, this._DOMNode);
    }, 0);
    this.resetClickDisabler();
  }

  componentWillUnmount() {
    // remove caching of DOM Node
    this._DOMNode = undefined;
  }

  resetClickDisabler() {
    this.disableClick = false;
    this.disableClickDiff = {
      x: 0,
      y: 0
    };
    zoomerActions.emit(zoomerActions.EVENTS.CLICK_RESET);
  }

  clickDisablerMove(diffX, diffY) {
    // call this when moving with your mouse
    if (!this.disableClick) {
      this.disableClickDiff.x += Math.abs(diffX);
      this.disableClickDiff.y += Math.abs(diffY);

      if (this.disableClickDiff.x + this.disableClickDiff.y > 10) { // 10 pixels, then it's a drag
        this.disableClick = true;
        zoomerActions.emit(zoomerActions.EVENTS.CLICK_DISABLE);
      }
    }
  }

  onMove(diffX, diffY, trackClick) {
    zoomerActions.emit(zoomerActions.EVENTS.MOVE, diffX, diffY);

    if (trackClick) {
      this.clickDisablerMove(diffX, diffY);
    }
  }

  // TODO, move in Zoomer Store?
  onScrollZoom(e) {
    // disabled ?
    if (config.system.scrollToZoom && !this.props.zoomer.disabled) {
      e.preventDefault();
      e.stopPropagation();

      // defer direction from scroll
      var deltaY = -1 * e.deltaY;
      var direction = deltaY && deltaY / Math.abs(deltaY);

      // get current dimensions from cached DOM Node
      var clientRect = this._DOMNode.getBoundingClientRect();

      // find the center
      var eventCenter = {
        x: e.clientX - clientRect.left,
        y: e.clientY - clientRect.top
      };
      // execute the zoom
      zoomerActions.emit(zoomerActions.EVENTS.ZOOM, direction, eventCenter);
    }
  }

  onPinch(touches) {
    // calculate the max zoom distance (75% of diagonal)
    // get current dimensions from cached DOM Node
    var clientRect = this._DOMNode.getBoundingClientRect();

    var pos1 = [touches[0].clientX - clientRect.left, touches[0].clientY - clientRect.top];
    var pos2 = [touches[1].clientX - clientRect.left, touches[1].clientY - clientRect.top];

    // detect the center of the pinch and the distance between fingers
    var newDist = pointDistance(pos1, pos2);
    var newCenter = pointCenter(pos1, pos2);

    // if this is the first action, just register the distance
    if (this.lastDist && this.lastCenter) {
      var opts = {
        lastDist:   this.lastDist,
        newDist:    newDist,
        lastCenter: this.lastCenter,
        newCenter:  newCenter
      };
      zoomerActions.emit(zoomerActions.EVENTS.PINCH, opts);
    }

    this.lastDist = newDist;
    this.lastCenter = newCenter;
  }

  onEnd() {
    // tell the world we are finishing a zoom action
    zoomerActions.emit(zoomerActions.EVENTS.END);
    this.lastDist = null;
    this.lastCenter = null;
  }

  onClick(e) {
    if (!this.props.zoomer.disabled && this.disableClick) {
      e.preventDefault();
      e.stopPropagation();
      this.resetClickDisabler();
    }
  }

  // Start the listening action
  // --------------------------------------------------------------------------
  start(e) {
    // tell the world we are starting a zoom action
    zoomerActions.emit(zoomerActions.EVENTS.START);

    if (!this.props.zoomer.disabled) {
      this.drgCtrl.start(e, this.props.zoomer.scale !== 1);
    }
  }

  getCSSTransform() {
    var {zoomer} = this.props;

    //if ie9 or less
    if (browserCheck.lessThanOrEqualIe9) {
      return `translate(${zoomer.translateX}px, ${zoomer.translateY}px)`;
    }

    return `translate3d(${zoomer.translateX}px, ${zoomer.translateY}px, 1px)`;
  }

  render() {
    var {zoomer} = this.props;
    var cssTransform = this.getCSSTransform();

    var zoomerProps = {
      style: {
        transform:       cssTransform,
        WebkitTransform: cssTransform,
        msTransform:     cssTransform,
        width:           `${zoomer.scale * 100}%`,
        height:          `${zoomer.scale * 100}%`
      },

      className:    'map__zoomer',
      tabIndex:     '1',
      onClick:      this.onClick,
      onWheel:      this.onScrollZoom,
      onMouseDown:  this.start,
      onTouchStart: this.start
    };

    return (
      <div data-component="zoomer" className="zoomer">
        <Controls />
        <div {...zoomerProps}>
          {this.props.children}
        </div>
      </div>
    );
  }
}

export default StoreConnect(SIMZoomer, {zoomer: zoomerStore});

/**
 * @param pos1
 * @param pos2
 * @returns {string}
 */
function pointDistance(pos1, pos2) {
  var xs = Math.pow(pos2[0] - pos1[0], 2);
  var ys = Math.pow(pos2[1] - pos1[1], 2);
  return Math.sqrt(xs + ys).toFixed();
}

/**
 * @param pos1
 * @param pos2
 * @returns {{x: number, y: number}}
 */
function pointCenter(pos1, pos2) {
  return {
    x: (pos1[0] + pos2[0]) / 2,
    y: (pos1[1] + pos2[1]) / 2
  };
}



// WEBPACK FOOTER //
// ./frontend/react/sim/core/components/zoomer/index.jsx